import { effect, inject, Injectable, isDevMode, NgZone, signal, untracked, WritableSignal } from '@angular/core';
import { Configuration } from '../models/interfaces/configuration/index.interface';
import { APP_CONFIG, ConfigurationValues } from '../configuration/configuration';
import { DOCUMENT, TitleCasePipe } from '@angular/common';
import { merge } from 'lodash-es';
import { DefaultConfig } from '@configuration/default-configuration';
import { defaultBranding } from '@configuration/default-configuration';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { validateUiConfig } from '@helpers/validators/ui-config.validator';
import { ToastService } from './toast.service';
import { TranslationService } from './translation.service';
import { ViewerMode } from '@models/enums/App/ViewerMode.enum';

@Injectable({
  providedIn: 'root',
})
export class ConfigurationService {

  // SERVICES 
  private readonly router: Router = inject(Router);
  
  private config:     WritableSignal<Configuration>  = inject(APP_CONFIG);
  private document:   Document                       = inject(DOCUMENT);
  private zone:       NgZone                         = inject(NgZone);
  private translation: TranslationService = inject(TranslationService);
  public currentUrl = signal(this.router.url);

  public isImportPreviewMode: WritableSignal<boolean> = signal(false); // Signal para evitar efectos de configuración en modo preview(importacion de sitios)
 
  // Listen to router navigation events and update currentUrl signal
  private readonly routerUrlEffect = effect(() => {
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd)
    ).subscribe((e: NavigationEnd) => {
      const previousUrl = this.currentUrl();
      const newUrl = e.urlAfterRedirects;

      // Check if URL is a team edit route (not dashboard routes)
      const isTeamEditRoute = (url: string) => {
        if (!url.startsWith('/admin/')) return false;
        const segment = url.substring('/admin/'.length);
        const dashboardRoutes = ['', 'list', 'translations', 'global-templates', 'new'];
        return !dashboardRoutes.includes(segment);
      };

      // If leaving a team edit route, reset config
      const leavingTeamEdit = isTeamEditRoute(previousUrl) && !isTeamEditRoute(newUrl);
      if (leavingTeamEdit) {
        this.resetConfiguration();
      }

      this.currentUrl.set(newUrl);
    });
  });

  // update title and favicon when currentUrl or config changes
  private readonly titleAndFaviconEffect = effect(() => {
    if((this.config() && this.currentUrl()) && !untracked(this.isImportPreviewMode)) this.zone.run(():void => {
      this.setTitle();
      this.setFavicon();
    });
  });

  private readonly toast = inject(ToastService);
  
  // CONFIGURATION
  // 
  public loadConfig(config: Configuration, mode?: ViewerMode): void {

    if (config) {
      // 1. Apply config
      this.applyConfiguration(config, mode);

      // 2. Apply css variables
      this.applyTheme();

      // 3. Add configuration proxy
      this.addConfigurationProxy();
    }

  }

  public togglePreviewMode(isPreview: boolean): void {
    this.isImportPreviewMode.set(isPreview);
  }

  public setImportedPreview(config: Configuration): void {
    this.config.set(config);
  }

  private applyConfiguration(config: Partial<Configuration>, mode?: ViewerMode): void {
    // Deep merge DefaultConfig with incoming config
    const mergedConfig: Configuration = merge({}, DefaultConfig, config);

    // If Ui exists, validate it and merge the validated result
    if (config.Ui) {
      const validatedUi = validateUiConfig(config.Ui, this.toast, mode!);
      mergedConfig.Ui = merge({}, mergedConfig.Ui, validatedUi);
    }
    this.config.set(mergedConfig);
  }

  public applyTheme(): void {
  
    const theme: Configuration['General']['theme'] = this.config().General.theme,
          root:  HTMLElement = this.document.documentElement;
    
    for(const [key, value] of Object.entries(theme)){
      root.style.setProperty(`--${key.split('_').join('-')}`, value);
    }
      
  }

  public setTitle(text?: string): void {

    const config = this.config();
    const pipe = new TitleCasePipe();
    const baseTitle = text ?? 'SBv3';
    const url = this.currentUrl().replace(/\/$/, '');

    let finalTitle: string;

    if (url === '/admin' || url === '/admin/list') { // If on '/admin' or '/admin/list', append " - Preview List" to base title
      finalTitle = `${baseTitle} - Preview List`;
    }
    else if (url === '/auth/login') { // If on '/login' or '/login/', append " - Login" to base title
      finalTitle = `${baseTitle} - Login`;
    }
    else if (url.startsWith('/admin/new')) { // If on '/admin/new', append " - New" to base title
      finalTitle = `${baseTitle} - New`;
    }
    else if (/^\/admin\/[^/]+/.test(url)) { // If on any other '/admin/:team' route, append the main configured text in Title Case
      const mainText = pipe.transform(config.General.texts.main ?? '');
      finalTitle = `${baseTitle} - ${mainText || 'new'}`;
    }
    else { // For all other routes (outside /admin), just use the configured main text or fallback to base title
      finalTitle = config.General.texts.main.length ? pipe.transform(config.General.texts.main) : baseTitle;
    }

    this.document.querySelector<HTMLTitleElement>('title')!.innerText = finalTitle;
  }

  public setFavicon(): void {
    const config = this.config();
    const url = this.currentUrl().replace(/\/$/, '');

    // Admin dashboard routes use default favicon, team edit and public routes use config favicon
    const isAdminDashboard = url === '/admin' || url === '/admin/list' || url === '/admin/translations' || url === '/admin/global-templates';
    const faviconUrl = isAdminDashboard || url === '/auth/login'
      ? defaultBranding.favicon
      : config.General.client.favicon;

    let linkEl = this.document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!linkEl) {
      linkEl = this.document.createElement('link');
      linkEl.rel = 'icon';
      this.document.head.appendChild(linkEl);
    }
    linkEl.href = faviconUrl;
  }

  public addConfigurationProxy(): void {

    if(!isDevMode()) return;

    // Funcion de proxy recursivo
    const reactiveProxy = <T extends object>(object: T, callback: () => void ): T => {
      
      return new Proxy(object, {

        // Si accedes a una propiedad que es un objeto, la envuelve en otro Proxy recursivamente.
        get(target, prop){
          
          const value = target[prop as keyof T];
          if (typeof value === "object" && value !== null) {
            return reactiveProxy(value, callback); // Aplica el proxy de forma recursiva
          }
          return value;
        },
        
        // Detecta si se ha modificado una propiedad y ejecuta el callback
        set(target, prop, value): boolean {
          console.log(`Configuración modificada: ${String(prop)} ->`, value);
          target[prop as keyof T] = value;
          callback();
          return true;
        },

      })
      
    }

    (window as {appConfig?: Configuration}).appConfig = reactiveProxy(this.config(), this.updateConfiguration.bind(this)); 
  } 

  private updateConfiguration(): void {

    if(!isDevMode()) return;

    this.zone.run(():void => {
      const newConfig: Configuration = JSON.parse(JSON.stringify((window as {appConfig?: Configuration}).appConfig));
      this.config.set( newConfig );
      this.applyTheme();
    });

  }

  public getMasterConfigBlob(): Blob {
    return new Blob([JSON.stringify(this.config())], { type: 'application/json' })
  }

  public resetConfiguration(): void {
    this.loadConfig(structuredClone(ConfigurationValues));
  }

}
